import React from "react";
import {
  LegacyCard,
  InlineGrid,
  TextField,
  Select,
  Checkbox,
} from "@shopify/polaris";
import CustomAutocomplete from "../CustomAutocomplete";
import {
  NameLabelColor,
  threadContrastColor,
  ButtonNumberShirt,
} from "../CoatData";
import { useLibrary } from "../LibraryContext";

const ShirtShortForm = ({
  form,
  setForm,
  errors,
  baseUrl,
  setApploader,
  conditionalFieldsMap,
  viewOrder
}) => {
  const { fabrics: Fabrics, linings: LiningNumbers } = useLibrary();
  const handleChange =
    (section, key = null) =>
      (values) => {
        const trimmedKey = key?.trim();
        let value;
        if (Array.isArray(values)) {
          value = values[0];
        } else {
          value = values;
        }

        const sectionMap = conditionalFieldsMap[section];
        const fieldRule = sectionMap?.[trimmedKey];

        if (fieldRule) {
          const { conditionValue, dependentKey } = fieldRule;
          if (value === conditionValue) {
            // Add the dependent field
            setForm((prev) => ({
              ...prev,
              [section]: {
                ...prev[section],
                [trimmedKey]: value,
                [dependentKey]: "",
              },
            }));
          } else {
            // Remove the dependent field
            const updatedSection = { ...form[section] };
            delete updatedSection[dependentKey];
            updatedSection[trimmedKey] = value;

            setForm((prev) => ({
              ...prev,
              [section]: updatedSection,
            }));
          }

          return;
        }
        // Default update
        if (trimmedKey) {
          setForm((prev) => ({
            ...prev,
            [section]: {
              ...prev[section],
              [trimmedKey]: value,
            },
          }));
        } else {
          setForm((prev) => ({
            ...prev,
            [section]: value,
          }));
        }
      };

  const ShirtShortFields = [
    {
      label: "Collar",
      key: "collar",
      type: "select",
      options: [
        "MR Classic",
        "MR Narrow",
        "MR Spread",
        "MR Wing",
        "MR Band",
        "MR Cuban",
      ],
    },
    {
      label: "Point Length",
      key: "pointLength",
      type: "select",
      options: ['2.75"', '2.5"', '3"', '3.25"'],
    },
    {
      label: "Front Height",
      key: "frontHeight",
      type: "select",
      options: ['1.25"', '1"', '1.5"', '2"'],
    },
    {
      label: "Back Height",
      key: "backHeight",
      type: "select",
      options: ['1"', '1.25"', '1.5"', '1.75"', '2"'],
    },
    {
      label: "Button Down",
      key: "buttonDown",
      type: "select",
      options: ["No", "Hidden Button Down", "Classic Button Down"],
    },
    {
      label: "Cuff",
      key: "cuff",
      type: "select",
      options: [
        "Plain",
        "Cuff",
      ],
    },
    {
      label: "Pocket",
      key: "pocket",
      type: "select",
      options: ["No", "Pointed", "Square", "Mitered"],
    },
    {
      label: "Pocket Flap",
      key: "pocketFlap",
      type: "select",
      options: ["No", "Pointed", "Mitered", "Round", "Square"],
    },
    {
      label: "Placket",
      key: "placket",
      type: "select",
      options: [
        "Placket",
        "Plain",
        "Hidden Button",
        "Tuxedo 4 Stud",
        "Polo Hidden",
        "Polo Showing",
      ],
    },
    {
      label: "Bottom Style",
      key: "bottomStyle",
      type: "select",
      options: ["Round", "Square with Vent"],
    },
    {
      label: "Placket Contrast",
      key: "placketContrast",
      type: "select",
      options: ["No", "Inside Placket", "Outside Placket"],
    },
    {
      label: "Button Number",
      key: "buttonNumber",
      type: "select",
      options: ButtonNumberShirt
    },
    {
      label: "Buttonhole Thread Front",
      key: "buttonholeThreadFront",
      type: "select",
      options: ["Match", "Contrast"],
    },
  ];

  const monogramOptions = [
    "Left Cuff",
    "Right Cuff",
    "Left Waist",
    "Left Chest",
    "Center of Inside Collar Band",
    "Inside Under Label",
    "Left Collar Point",
    "Pocket Top Center",
  ];

  // 3. Add a change handler for monogram checkboxes
  const handleMonogramChange = (label, checked) => {
    setForm((prev) => {
      const updated = checked
        ? [...prev.shirtShortSleeve.monograms, label]
        : prev.shirtShortSleeve.monograms.filter((m) => m !== label);

      const updatedDetails = { ...prev.shirtShortSleeve.monogramDetails };
      if (checked && !updatedDetails[label]) {
        updatedDetails[label] = {
          font: "Calligraphy",
          color: "102 White",
          size: "Regular",
          text: "",
        };
      }
      if (!checked) delete updatedDetails[label];

      return {
        ...prev,
        shirtShortSleeve: {
          ...prev.shirtShortSleeve,
          monograms: updated,
          monogramDetails: updatedDetails,
        },
      };
    });
  };

  const handleMonogramFieldChange = (label, key, value) => {
    setForm((prev) => ({
      ...prev,
      shirtShortSleeve: {
        ...prev.shirtShortSleeve,
        monogramDetails: {
          ...prev.shirtShortSleeve.monogramDetails,
          [label]: {
            ...prev.shirtShortSleeve.monogramDetails[label],
            [key]: value,
          },
        },
      },
    }));
  };

  const handleFileUpload = async (section, key, file) => {
    const formData = new FormData();
    formData.append("image", file);
    setApploader(true);
    try {
      const res = await fetch(`${baseUrl}/api/files/upload`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        const uploadedFilename = data.files;
        handleChange(section, key)(uploadedFilename);
        if (key === "fileimage") {
          handleMonogramFieldChange(section, key, uploadedFilename);
        }
        setApploader(false);
      } else {
        alert("File upload failed");
      }
    } catch (error) { }
  };

  const handleDownload = async (url) => {
    try {
      const response = await fetch(url, { mode: "cors" });
      if (!response.ok) throw new Error("Network response was not ok");
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = url.split("/").pop() || "download.png";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
    }

  };

  const renderSection = (title, fields, sectionKey) => (
    <LegacyCard title={title} sectioned>
      <InlineGrid columns={{ xs: 1, sm: 1, md: 2, lg: 3 }} gap="300">
        {fields.map(({ label, key, type = "text", options = [] }) => {
          if (type === "select") {
            return (
              <React.Fragment key={key}>
                {key !== "buttonNumber" &&
                  key !== "fabric" && (
                    <div className="two_culumn">
                      <Select
                        label={label}
                        options={options}
                        value={form[sectionKey]?.[key] || ""}
                        onChange={handleChange(sectionKey, key)}
                        error={errors[sectionKey]?.[key]}
                      />
                      {key === "collarContrast" && form[sectionKey]?.[key] !== "No" && (
                        <CustomAutocomplete
                          placeholder="Fabric Number "
                          optionsList={Fabrics}
                          onChange={handleChange(sectionKey, "collarContrastFabricNumber")}
                          defaultValue={
                            form[sectionKey]?.["collarContrastFabricNumber"] || ""
                          }
                        />
                      )}
                      {key === "placketContrast" &&
                        form[sectionKey]?.[key] !== "No" && (
                          <CustomAutocomplete
                            placeholder="Fabric Number "
                            optionsList={Fabrics}
                            onChange={handleChange(sectionKey, "placketContrastFabricNumber")}
                            defaultValue={
                              form[sectionKey]?.["placketContrastFabricNumber"] || ""
                            }
                          />
                        )}
                      {key === "cuffContrast" && form[sectionKey]?.[key] !== "No" && (
                        <CustomAutocomplete
                          placeholder="Fabric Number"
                          optionsList={Fabrics}
                          onChange={handleChange(sectionKey, "cuffContrastFabricNumber")}
                          defaultValue={
                            form[sectionKey]?.["cuffContrastFabricNumber"] || ""
                          }
                        />
                      )}
                      {key === "buttonholeThreadFront" &&
                        form[sectionKey]?.[key] === "Contrast" && (
                          <CustomAutocomplete
                            placeholder="Sewing Thread Number"
                            optionsList={threadContrastColor}
                            onChange={handleChange(sectionKey, "buttonholeThreadFrontThreadNumber")}
                            defaultValue={
                              form[sectionKey]?.["buttonholeThreadFrontThreadNumber"] || ""
                            }
                          />
                        )}
                    </div>
                  )}
                {key === "buttonNumber" && (
                  <CustomAutocomplete
                    label={label}
                    optionsList={options}
                    onChange={handleChange(sectionKey, key)}
                    defaultValue={form[sectionKey]?.[key] || ""}
                  />
                )}
                {key === "fabric" && (
                  <CustomAutocomplete
                    label={label}
                    optionsList={Fabrics}
                    onChange={handleChange(sectionKey, key)}
                    defaultValue={form[sectionKey]?.[key] || ""}
                  />
                )}
              </React.Fragment>
            );
          }
          return (
            <TextField
              key={key}
              label={label}
              value={form[sectionKey]?.[key] || ""}
              onChange={handleChange(sectionKey, key)}
              error={errors[sectionKey]?.[key]}
            />
          );
        })}
      </InlineGrid>
      <div className="multiple_option_checkbox">
        <h3>Add Monogram</h3>
        <InlineGrid columns={{ xs: 2, sm: 2, md: 2, lg: 4 }} gap="100">
          {monogramOptions.map((label) => (
            <Checkbox
              key={label}
              label={label}
              checked={form.shirtShortSleeve.monograms.includes(label)}
              onChange={(val) => handleMonogramChange(label, val)}
            />
          ))}
        </InlineGrid>
      </div>
      {form.shirtShortSleeve.monograms.map((label, index) => (
        <div
          key={`monogram-${label}-${index}`}
          className="monogram_option_list"
        >
          <h3>{`Monogram Details - ${label}`}</h3>
          <InlineGrid columns={{ xs: 1, sm: 2, md: 2, lg: 4 }} gap="400">
            <div>
              <Select
                label="Font"
                options={[
                  "Caligraphy",
                  "Times Roman",
                  "Block",
                  "Script",
                  "Bradley",
                  "Old English",
                  "Custom Upload",
                ]}
                value={form.shirtShortSleeve.monogramDetails[label]?.font || ""}
                onChange={(val) =>
                  handleMonogramFieldChange(label, "font", val)
                }
              />
              {form.shirtShortSleeve.monogramDetails[label]?.font ===
                "Custom Upload" ? (
                <input
                  label="Upload Image"
                  className="fileUpload"
                  type="file"
                  accept=".jpg, .jpeg, .png"
                  name="image"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    handleFileUpload(label, "fileimage", file);
                  }}
                />
              ) : null}
              {viewOrder === 'yes' && (
                form.shirtShortSleeve.monogramDetails[label]?.fileimage ? (
                  <img
                    className="image_server"
                    onClick={() => handleDownload(form?.shirtShortSleeve?.monogramDetails?.[label]?.fileimage || "")}
                    src={form.shirtShortSleeve.monogramDetails[label].fileimage}
                    alt="Mark Russell"
                  />
                ) : null
              )}
            </div>
            <Select
              label="Color"
              options={NameLabelColor}
              value={form.shirtShortSleeve.monogramDetails[label]?.color || ""}
              onChange={(val) => handleMonogramFieldChange(label, "color", val)}
            />
            <Select
              label="Size"
              options={["Regular", "Small", "Large", "Extra Large"]}
              value={form.shirtShortSleeve.monogramDetails[label]?.size || ""}
              onChange={(val) =>
                handleMonogramFieldChange(label, "size", val)
              }
            />
            {form.shirtShortSleeve.monogramDetails[label]?.font !== "Custom Upload" && (
              <TextField
                label="Text"
                value={form.shirtShortSleeve.monogramDetails[label]?.font ? form.shirtShortSleeve.monogramDetails[label]?.text || "" : ""}
                onChange={(val) => handleMonogramFieldChange(label, "text", val)}
                error={errors.shirtShortSleeve?.monogramDetails?.[label]?.text}
              />
            )}
          </InlineGrid>
        </div>
      ))}
    </LegacyCard>
  );

  return renderSection("Shirt Short Sleeve", ShirtShortFields, "shirtShortSleeve");
};

export default ShirtShortForm;
